import { useEffect, useRef, useState } from "react";
import { Button, Checkbox } from "@legal-500/legal500-design-system";
import {
  users as usersService,
  firms as firmsService,
  type UserRow,
  type Firm,
} from "../../../api/userVerification.service.js";
import {
  developerUsersService,
  type MoveToFirmResponse,
} from "../../../api/developerUsers.service.js";
import {
  accountRemediationService,
  type ReassignPrecheckResponse,
} from "../../../api/accountRemediation.service.js";
import { isGroupSelectable, buildMoveToFirmBody } from "./moveToFirmScoping.js";
import { getPrecheckTargetId } from "../AccountRemediation/remediation.js";
import styles from "./Users.module.css";

// ---------------------------------------------------------------------------
// Generic inline search-dropdown component
// ---------------------------------------------------------------------------

type SearchDropdownProps<T> = {
  id: string;
  label: string;
  placeholder: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  results: T[];
  isLoading: boolean;
  fetchError: string | null;
  renderLabel: (item: T) => string;
  renderMeta: (item: T) => string;
  onSelect: (item: T) => void;
  selectedItem: T | null;
  renderSelectedLabel: (item: T) => string;
  onClearSelection: () => void;
  disabled?: boolean;
};

function SearchDropdown<T extends { toString(): string }>({
  id,
  label,
  placeholder,
  searchQuery,
  onSearchChange,
  results,
  isLoading,
  fetchError,
  renderLabel,
  renderMeta,
  onSelect,
  selectedItem,
  renderSelectedLabel,
  onClearSelection,
  disabled = false,
}: SearchDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Open dropdown whenever there is something to show
  useEffect(() => {
    if (isLoading || fetchError || results.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isLoading, fetchError, results]);

  return (
    <div className={styles.formGroup}>
      <label htmlFor={id} className={styles.formLabel}>
        {label}
      </label>

      {selectedItem ? (
        <div className={styles.selectedBadge}>
          <span>{renderSelectedLabel(selectedItem)}</span>
          {!disabled && (
            <button
              type="button"
              className={styles.selectedBadgeClear}
              onClick={onClearSelection}
              aria-label={`Clear ${label} selection`}
            >
              &times;
            </button>
          )}
        </div>
      ) : (
        <div className={styles.searchWrapper} ref={wrapperRef}>
          <input
            id={id}
            type="text"
            className={styles.formInput}
            placeholder={placeholder}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            onFocus={() => {
              if (results.length > 0 || isLoading || fetchError) setIsOpen(true);
            }}
            disabled={disabled}
            autoComplete="off"
          />

          {isOpen && (
            <ul className={styles.searchDropdown} role="listbox">
              {isLoading && (
                <li className={styles.searchDropdownStatus} role="option" aria-selected={false}>
                  Searching...
                </li>
              )}
              {!isLoading && fetchError && (
                <li className={styles.searchDropdownStatus} role="option" aria-selected={false}>
                  {fetchError}
                </li>
              )}
              {!isLoading && !fetchError && results.length === 0 && searchQuery.trim() && (
                <li className={styles.searchDropdownStatus} role="option" aria-selected={false}>
                  No results found.
                </li>
              )}
              {!isLoading &&
                !fetchError &&
                results.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index} role="option" aria-selected={false}>
                    <button
                      type="button"
                      className={styles.searchDropdownItem}
                      onMouseDown={e => {
                        // prevent blur from firing before click
                        e.preventDefault();
                        onSelect(item);
                        setIsOpen(false);
                      }}
                    >
                      <span>{renderLabel(item)}</span>
                      <span className={styles.searchDropdownMeta}>{renderMeta(item)}</span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MoveToFirmTab
// ---------------------------------------------------------------------------

function MoveToFirmTab() {
  // Step 1 — user
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<UserRow[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userFetchError, setUserFetchError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const userDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2 — target firm
  const [newFirmQuery, setNewFirmQuery] = useState("");
  const [newFirmResults, setNewFirmResults] = useState<Firm[]>([]);
  const [newFirmLoading, setNewFirmLoading] = useState(false);
  const [newFirmFetchError, setNewFirmFetchError] = useState<string | null>(null);
  const [selectedNewFirm, setSelectedNewFirm] = useState<Firm | null>(null);
  const newFirmDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<MoveToFirmResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reassign pre-check + scoping opt-in
  const [precheck, setPrecheck] = useState<ReassignPrecheckResponse | null>(null);
  const [precheckError, setPrecheckError] = useState<string | null>(null);
  const [scoped, setScoped] = useState(false);
  const [selectedFirmRefs, setSelectedFirmRefs] = useState<Set<string>>(new Set());

  // ------- debounced search effects -------

  useEffect(() => {
    if (userDebounceRef.current) clearTimeout(userDebounceRef.current);
    if (!userQuery.trim()) {
      setUserResults([]);
      setUserLoading(false);
      setUserFetchError(null);
      return;
    }
    userDebounceRef.current = setTimeout(async () => {
      setUserLoading(true);
      setUserFetchError(null);
      try {
        const res = await usersService.list({ search: userQuery.trim(), limit: 10, offset: 0 });
        setUserResults(res.data);
      } catch {
        setUserFetchError("Failed to search users. Please try again.");
      } finally {
        setUserLoading(false);
      }
    }, 300);
    return () => {
      if (userDebounceRef.current) clearTimeout(userDebounceRef.current);
    };
  }, [userQuery]);

  useEffect(() => {
    if (newFirmDebounceRef.current) clearTimeout(newFirmDebounceRef.current);
    if (!newFirmQuery.trim()) {
      setNewFirmResults([]);
      setNewFirmLoading(false);
      setNewFirmFetchError(null);
      return;
    }
    newFirmDebounceRef.current = setTimeout(async () => {
      setNewFirmLoading(true);
      setNewFirmFetchError(null);
      try {
        const res = await firmsService.search(newFirmQuery.trim());
        setNewFirmResults(res.data);
      } catch {
        setNewFirmFetchError("Failed to search firms. Please try again.");
      } finally {
        setNewFirmLoading(false);
      }
    }, 300);
    return () => {
      if (newFirmDebounceRef.current) clearTimeout(newFirmDebounceRef.current);
    };
  }, [newFirmQuery]);

  // Once a user is selected, check whether their submissions span more than one firm — this
  // must target the Frontegg external id (never the internal user id), or the pre-check silently
  // returns empty arrays that look identical to "no spread".
  useEffect(() => {
    setPrecheck(null);
    setPrecheckError(null);
    setScoped(false);
    setSelectedFirmRefs(new Set());

    const externalId = selectedUser ? getPrecheckTargetId(selectedUser) : null;
    if (!externalId) return;

    let cancelled = false;
    void (async () => {
      try {
        const response = await accountRemediationService.getReassignPrecheck(externalId);
        if (!cancelled) setPrecheck(response);
      } catch {
        if (!cancelled) setPrecheckError("Failed to check this user's submission spread.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedUser]);

  // ------- derived values -------

  const userId = selectedUser ? (selectedUser.externalId ?? selectedUser.id) : "";
  const firmRef = selectedNewFirm ? (selectedNewFirm.firmRef ?? "") : "";
  const firmName = selectedNewFirm?.name ?? "";

  const canSubmit =
    userId !== "" && firmRef !== "" && !isSubmitting && !(scoped && selectedFirmRefs.size === 0);

  const toggleSelectedFirmRef = (ref: string, checked: boolean) => {
    setSelectedFirmRefs(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(ref);
      } else {
        next.delete(ref);
      }
      return next;
    });
  };

  // ------- submit -------

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setResult(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const body = buildMoveToFirmBody({
        userId,
        firmRef,
        firmName,
        scoped,
        selectedFirmRefs: [...selectedFirmRefs],
      });
      if (body === null) {
        setSubmitError("Select at least one firm group, or turn off scoping to move everything");
        return;
      }
      const response = await developerUsersService.moveUserToFirm(body);
      setResult(response);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to move user to firm");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={styles.content}>
      <form onSubmit={e => void handleSubmit(e)} className={styles.moveForm}>
        {/* Step 1: User */}
        <SearchDropdown<UserRow>
          id="userSearch"
          label="Step 1: User"
          placeholder="Search by name or email..."
          searchQuery={userQuery}
          onSearchChange={value => {
            setUserQuery(value);
            setUserResults([]);
          }}
          results={userResults}
          isLoading={userLoading}
          fetchError={userFetchError}
          renderLabel={u => `${u.name} (${u.email})`}
          renderMeta={u => u.externalId ?? u.id}
          onSelect={u => {
            setSelectedUser(u);
            setUserQuery("");
            setUserResults([]);
          }}
          selectedItem={selectedUser}
          renderSelectedLabel={u => `${u.name} — ${u.email}`}
          onClearSelection={() => {
            setSelectedUser(null);
            setUserQuery("");
            setUserResults([]);
          }}
          disabled={isSubmitting}
        />

        {/* Step 2: Target firm */}
        <SearchDropdown<Firm>
          id="newFirmSearch"
          label="Step 2: Target firm"
          placeholder="Search by firm name..."
          searchQuery={newFirmQuery}
          onSearchChange={value => {
            setNewFirmQuery(value);
            setNewFirmResults([]);
          }}
          results={newFirmResults}
          isLoading={newFirmLoading}
          fetchError={newFirmFetchError}
          renderLabel={c => c.name}
          renderMeta={c => c.firmRef ?? "no ref"}
          onSelect={c => {
            setSelectedNewFirm(c);
            setNewFirmQuery("");
            setNewFirmResults([]);
          }}
          selectedItem={selectedNewFirm}
          renderSelectedLabel={c => (c.firmRef !== null ? `${c.name} (${c.firmRef})` : c.name)}
          onClearSelection={() => {
            setSelectedNewFirm(null);
            setNewFirmQuery("");
            setNewFirmResults([]);
          }}
          disabled={isSubmitting}
        />

        {selectedNewFirm && (
          <div className={styles.derivedInfo}>
            <p className={styles.derivedInfoRow}>
              <strong>Firm ref:</strong> {selectedNewFirm.firmRef ?? "—"}
            </p>
            <p className={styles.derivedInfoRow}>
              <strong>Firm name:</strong> {selectedNewFirm.name}
            </p>
          </div>
        )}

        {precheckError && <div className={styles.errorBlock}>{precheckError}</div>}

        {precheck?.multiFirm && (
          <div role="alert" className={`${styles.notification} ${styles.error}`}>
            This user&apos;s submissions span more than one firm — review before moving everything.
          </div>
        )}

        {precheck && precheck.submissionsByFirmRef.length > 0 && (
          <div className={styles.formGroup}>
            <Checkbox
              id="scope-move-to-firm"
              label="Only move specific firm groups"
              aria-label="Only move specific firm groups"
              checked={scoped}
              onChange={event => setScoped(event.target.checked)}
              disabled={isSubmitting}
            />

            {scoped && (
              <ul className={styles.tableContainer}>
                {precheck.submissionsByFirmRef.map(group => {
                  const selectable = isGroupSelectable(group.firmRef);
                  const label =
                    group.firmRef && group.firmRef !== ""
                      ? `${group.firmRef} (${group.count})`
                      : `blank/null (${group.count})`;
                  return (
                    <li key={group.firmRef || "blank"}>
                      <Checkbox
                        id={`scope-group-${group.firmRef || "blank"}`}
                        label={label}
                        aria-label={`Select ${label}`}
                        checked={selectable && selectedFirmRefs.has(group.firmRef)}
                        disabled={!selectable || isSubmitting}
                        onChange={event =>
                          toggleSelectedFirmRef(group.firmRef, event.target.checked)
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div>
          <Button type="submit" variant="contained" color="primary" disabled={!canSubmit}>
            {isSubmitting ? "Moving..." : "Move to Firm"}
          </Button>
        </div>
      </form>

      {submitError && <div className={styles.errorBlock}>{submitError}</div>}

      {result && (
        <div className={styles.successBlock}>
          <p className={styles.successBlockTitle}>User moved successfully</p>
          <p className={styles.successBlockRow}>
            User <strong>{result.frontegg.userId}</strong> bound to firm{" "}
            <strong>{result.frontegg.firmRef}</strong>
          </p>
          <p className={styles.successBlockRow}>
            Submissions updated: {result.submissions.updatedSubmissions}
          </p>
          <p className={styles.successBlockRow}>
            Document files updated: {result.submissions.updatedDocumentFiles}
          </p>
          <p className={styles.successBlockRow}>
            Referee files updated: {result.submissions.updatedRefereeFiles}
          </p>
          {(result.submissions.skippedSubmissions ?? 0) > 0 && (
            <p className={styles.successBlockRow}>
              Skipped (duplicate): {result.submissions.skippedSubmissions}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default MoveToFirmTab;
